import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { practiceAPI } from '../../api';
import { MathJaxContext, MathJax } from 'better-react-mathjax';

// ✅ FIXED: improved detection – only wrap if there's a backslash command, superscript ^, or valid subscript pattern (e.g., x_1)
const wrapMathIfNeeded = (text) => {
  if (!text) return text;
  // Detect LaTeX commands (e.g., \frac, \sqrt), superscript ^, or valid subscript (letter/digit + _ + letter/digit/{)
  const hasLatex = /\\[a-zA-Z]+/.test(text) ||
                   /\^/.test(text) ||
                   /[a-zA-Z0-9]_[a-zA-Z0-9{}]/.test(text);
  if (hasLatex) {
    const escaped = text.replace(/ /g, '\\ ');
    // Avoid double wrapping if already has \(...\)
    if (!/^\\\(/.test(text)) {
      return `\\(${escaped}\\)`;
    }
    return escaped;
  }
  return text;
};

const PracticeSession = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [practiceSet, setPracticeSet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [answerResults, setAnswerResults] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [mode, setMode] = useState('immediate');
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jumpInput, setJumpInput] = useState('');
  const questionRefs = useRef([]);

  useEffect(() => {
    const loadSet = async () => {
      try {
        const res = await practiceAPI.getSet(setId);
        setPracticeSet(res.data);
        const qs = res.data.questions || [];
        setQuestions(qs);
        const sessionRes = await practiceAPI.startSession(setId);
        setSessionId(sessionRes.data.session_id);
      } catch (err) {
        toast.error('Failed to load practice set');
        navigate('/participate/preparena');
      } finally {
        setLoading(false);
      }
    };
    loadSet();
  }, [setId, navigate]);

  const checkAnswer = (question, selectedLetter) => selectedLetter === question.answer;

  const handleAnswerByIndex = (idx, selectedLetter) => {
    const q = questions[idx];
    const isCorrect = checkAnswer(q, selectedLetter);

    setUserAnswers(prev => ({ ...prev, [idx]: selectedLetter }));
    setAnswerResults(prev => ({ ...prev, [idx]: isCorrect ? 'correct' : 'wrong' }));

    if (mode === 'immediate') {
      toast(isCorrect ? '✅ Correct!' : '❌ Wrong!', {
        type: isCorrect ? 'success' : 'error',
        autoClose: 1500,
      });
    }
  };

  const calculateScore = () => {
    let correctCount = 0;
    questions.forEach((q, idx) => {
      const selected = userAnswers[idx];
      if (selected && checkAnswer(q, selected)) correctCount++;
    });
    const total = questions.length;
    const percent = total === 0 ? 0 : (correctCount / total) * 100;
    return { correctCount, total, percent };
  };

  const submitPractice = async () => {
    if (submitting) return;
    setSubmitting(true);
    const { correctCount, total, percent } = calculateScore();
    setShowResults(true);
    try {
      await practiceAPI.submitSession(sessionId, percent, correctCount);
    } catch (err) {
      console.error('Failed to save session', err);
    }
    setSubmitting(false);
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) setCurrentIndex(prev => prev + 1);
    else submitPractice();
  };

  const prevQuestion = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
      setJumpInput('');
    }
  };

  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(jumpInput, 10);
    if (!isNaN(num) && num >= 1 && num <= questions.length) {
      goToQuestion(num - 1);
    } else {
      toast.error(`Enter a number between 1 and ${questions.length}`);
    }
  };

  const restart = () => {
    navigate(0);
  };

  const getQuestionStatus = (idx) => {
    if (userAnswers[idx] === undefined) return 'unanswered';
    return answerResults[idx] === 'correct' ? 'correct' : 'wrong';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showResults) {
    const { correctCount, total, percent } = calculateScore();
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 text-center">
          <div className="mb-6">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${percent >= 70 ? 'bg-green-100' : 'bg-orange-100'}`}>
              <svg className={`w-12 h-12 ${percent >= 70 ? 'text-green-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Practice Completed!</h2>
          <p className="text-gray-600 mb-6">You answered {correctCount} out of {total} correctly.</p>
          <div className="text-5xl font-bold mb-6">{Math.round(percent)}%</div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={restart} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition">Practice Again</button>
            <button onClick={() => navigate('/participate/preparena')} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition">Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <h2 className="text-xl font-bold text-red-600">No questions found</h2>
          <button onClick={() => navigate('/participate/preparena')} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition">Go Back</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const currentAnswer = userAnswers[currentIndex];
  const currentResult = answerResults[currentIndex];
  const totalQuestions = questions.length;

  return (
    <MathJaxContext
      config={{
        tex: {
          inlineMath: [
            ['$', '$'],
            ['\\(', '\\)']
          ],
          displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]']
          ]
        }
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-4 sm:py-8 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with progress and mode */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{practiceSet?.title}</h1>
                <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Question {currentIndex+1} of {totalQuestions}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs sm:text-sm">
                <label className="flex items-center gap-1">
                  <input type="radio" name="mode" checked={mode === 'immediate'} onChange={() => setMode('immediate')} className="w-3.5 h-3.5" />
                  Immediate
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="mode" checked={mode === 'final'} onChange={() => setMode('final')} className="w-3.5 h-3.5" />
                  At end
                </label>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex+1)/totalQuestions)*100}%` }}
              ></div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700 mr-1">Jump to:</span>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1">
                  {questions.map((_, idx) => {
                    const status = getQuestionStatus(idx);
                    let bgColor = 'bg-gray-200 hover:bg-gray-300';
                    let ring = '';
                    if (status === 'correct') bgColor = 'bg-green-500 text-white hover:bg-green-600';
                    else if (status === 'wrong') bgColor = 'bg-red-500 text-white hover:bg-red-600';
                    if (idx === currentIndex) ring = 'ring-2 ring-blue-500 ring-offset-2';
                    return (
                      <button
                        key={idx}
                        onClick={() => goToQuestion(idx)}
                        className={`w-8 h-8 rounded-full text-xs font-medium transition-all flex items-center justify-center ${bgColor} ${ring}`}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
              <form onSubmit={handleJumpSubmit} className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={totalQuestions}
                  value={jumpInput}
                  onChange={(e) => setJumpInput(e.target.value)}
                  placeholder="Q#"
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                >
                  Go
                </button>
              </form>
            </div>
            {/* Legend (optional) */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-600">
              <span><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span> Correct</span>
              <span><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span> Wrong</span>
              <span><span className="inline-block w-3 h-3 rounded-full bg-gray-200 mr-1"></span> Unanswered</span>
              <span><span className="inline-block w-3 h-3 rounded-full border-2 border-blue-500 mr-1"></span> Current</span>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8">
            <div className="mb-6 text-base sm:text-lg md:text-xl font-medium text-gray-800">
              <MathJax inline>{wrapMathIfNeeded(currentQ.question)}</MathJax>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQ.options.map((opt) => {
                const isSelected = currentAnswer === opt.option;
                let borderColor = 'border-gray-200';
                let bgColor = '';
                if (isSelected && currentResult) {
                  borderColor = currentResult === 'correct' ? 'border-green-500' : 'border-red-500';
                  bgColor = currentResult === 'correct' ? 'bg-green-50' : 'bg-red-50';
                } else if (isSelected) {
                  borderColor = 'border-blue-400';
                  bgColor = 'bg-blue-50';
                }

                return (
                  <label
                    key={opt.option}
                    className={`flex items-start p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${borderColor} ${bgColor}`}
                  >
                    <input
                      type="radio"
                      name="question"
                      value={opt.option}
                      checked={isSelected}
                      onChange={() => handleAnswerByIndex(currentIndex, opt.option)}
                      className="mt-1 mr-3 shrink-0"
                    />
                    <div className="flex-1 text-sm sm:text-base">
                      <span className="font-semibold mr-2">{opt.option}.</span>
                      <MathJax inline>{wrapMathIfNeeded(opt.content)}</MathJax>
                    </div>
                    {isSelected && currentResult && (
                      <span className="ml-2 text-xl">
                        {currentResult === 'correct' ? '✅' : '❌'}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            {/* Navigation buttons */}
            <div className="flex flex-col sm:flex-row justify-between mt-8 pt-6 border-t border-gray-200 gap-3">
              <button
                onClick={prevQuestion}
                disabled={currentIndex === 0}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition text-sm sm:text-base"
              >
                Previous
              </button>
              <button
                onClick={nextQuestion}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm sm:text-base"
              >
                {currentIndex === totalQuestions-1 ? 'Submit' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
};

export default PracticeSession;